import { Button, Card, Heading, Input, Text, toast } from "@overmap-ai/blocks"
import {
	type Form,
	OrganizationAccessLevel,
	type Payload,
	ABBREVIATION_MAX_LENGTH,
	ABBREVIATION_MIN_LENGTH,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError, selectFormById } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { RouteLinksCard } from "../../components/RouteLinksCard/RouteLinksCard"
import type { RouteLinksCardRoute } from "../../components/RouteLinksCard/typings"
import { TITLE_MAX_LENGTH } from "../../constants"
import { useTextConfirmationDialog } from "../../contexts/hooks"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/forms/$formId")

const routes: RouteLinksCardRoute[] = [
	{
		icon: modelIconMapping.identifier,
		label: "Identifiers",
		description: "Manage identifiers such as QR codes and barcodes",
		from: "/$organizationId/settings/forms/$formId",
		to: "/$organizationId/settings/forms/$formId/identifiers",
	},
	{
		icon: modelIconMapping.form,
		label: "Fields",
		description: "Manage form fields",
		from: "/$organizationId/settings/forms/$formId",
		to: "/$organizationId/settings/forms/$formId/fields",
	},
]

export const FormSettingsDetailView = memo(() => {
	const { formId } = routeApi.useParams()
	const form = useAppSelector(selectFormById(formId))

	if (!form) {
		throw new Error(`expected form to be defined, got ${form}`)
	}

	const navigate = useNavigate()
	const openTextConfirmationDialog = useTextConfirmationDialog()
	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handlePatchForm = useCallback(
		(payload: Partial<Payload<Form>>) => {
			void sdk.forms.update({ uuid: form.uuid, ...payload })
		},
		[form.uuid],
	)

	const handleNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			handlePatchForm({ name: e.target.value })
		},
		[handlePatchForm],
	)

	const nameInputProps = useBlurInput<HTMLInputElement>(form.name, handleNameInputBlur, true)

	const handleAbbrInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value
			handlePatchForm({ abbr: value })
		},
		[handlePatchForm],
	)

	const abbrInputProps = useBlurInput<HTMLInputElement>(form.abbr, handleAbbrInputBlur, true)

	const handleDelete = useCallback(() => {
		openTextConfirmationDialog((close) => ({
			title: `Delete ${form.name}`,
			description: "Are you sure you want to delete this form?",
			confirmString: "delete",
			confirmMessage: "Type 'delete' to confirm",
			actionLabel: "Delete",
			onClose: close,
			onConfirm: async () => {
				await navigate({
					from: "/$organizationId/settings/forms/$formId",
					to: "/$organizationId/settings/forms",
				})

				sdk.forms.delete(form.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete form",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
			},
		}))
	}, [form.uuid, form.name, navigate, openTextConfirmationDialog])

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{form.name}</Heading>
					<Text size="sm" accentColor="base">
						Manage form settings and fields.
					</Text>
				</div>

				<Card className="flex w-full flex-col gap-1" size="sm">
					<div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-2">
						<div className="flex w-full flex-col gap-1">
							<Text accentColor="base" size="sm">
								Name
							</Text>

							<Input.Root className="grow" variant="outline">
								<Input.Field
									placeholder="Enter a name"
									maxLength={TITLE_MAX_LENGTH}
									disabled={!isOrganizationAdmin}
									{...nameInputProps}
								/>
							</Input.Root>
						</div>

						<div className="flex w-full flex-col gap-1">
							<Text accentColor="base" size="sm">
								Abbreviation
							</Text>

							<Input.Root className="grow" variant="outline">
								<Input.Field
									minLength={ABBREVIATION_MIN_LENGTH}
									max={ABBREVIATION_MAX_LENGTH}
									placeholder="Enter an abbreviation"
									disabled={!isOrganizationAdmin}
									{...abbrInputProps}
								/>
							</Input.Root>
						</div>
					</div>
				</Card>

				<RouteLinksCard routes={routes} />

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<Button className="w-max" size="sm" accentColor="danger" onClick={handleDelete}>
						Delete
					</Button>
				</OrganizationAccessRequired>
			</section>
		</ViewContainer>
	)
})

FormSettingsDetailView.displayName = "FormSettingsDetailView"
